import "./transaction-page.scss";
import {observer} from 'mobx-react';
import {useCallback, useEffect} from 'react';
import {useSearchParams} from "react-router-dom";
import {SingleValue} from 'react-select';
import AHref from '../../../components/ahref/ahref';
import { useDateTimeFormattingParams } from "../../../hooks/use-datetime-formatting-params";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import FontBody2 from '../../../components/fonts/font-body2/font-body2';
import {FONT_COLOR} from '../../../components/fonts/font-props';
import {IOption, SelectDropdown} from '../../../components/inputs/select-dropdown/select-dropdown';
import BasePageLayout from '../../../components/layouts/base-page-layout/base-page-layout';
import Card from '../../../components/layouts/card/card';
import ReportBoxLayout from "../../../components/layouts/report-box-layout/report-box-layout";
import SearchBar from '../../../components/layouts/search-bar/search-bar';
import Report from "../../../components/report/report";
import SearchInput from '../../../components/search-input/search-input';
import TableRow from '../../../components/table/components/table-row/table-row';
import Table from '../../../components/table/table';
import {IColumn} from "../../../components/table/table.types";
import {REPORT_TYPES} from "../../../services/api/report-service";
import {ITransaction, TRANSACTION_STATUSES, TRANSACTION_TYPES} from '../../../services/api/transactions-service';
import currentAccountStore from "../../accounts/stores/current-account-store";
import Amount from '../components/amount/amount';
import GiftCardsHeadCellContent from './components/gift-cards-head-cell-content/gift-cards-head-cell-content';
import TansactionType from './components/transaction-type/transaction-type';
import pagedListStore from './transactions-paged-list-store';
import Chip, { CHIP_COLORS } from "../../../components/chip/chip";
import Currency from "../../../components/currency/currency";
import currentPortalStore from "../../../stores/current-portal-store";
import DateTimeV2, {DATETIME_VIEW} from "../../../components/datetime-v2/datetime-v2";
import DateRangePickerV2, {IDateRangeISO} from "../../../components/date-range-picker-v2/date-range-picker-v2";

const TransactionsPage: React.FC = observer(() => {
    const [searchParams] = useSearchParams();
    const dateTimeParams = useDateTimeFormattingParams();

    useEffect(() => {
        pagedListStore.setSearchCustomerId(searchParams.get('filter_customer_id') ?? '');
        searchParams.delete('filter_customer_id');

        pagedListStore.loadPage(1);
        currentAccountStore.load();
    }, [searchParams]);

    const onSearchClick = useCallback(() => pagedListStore.loadPage(1), []);

    const onSearchChange = useCallback((value: string) => {
        pagedListStore.setSearchCustomerId(value);
    }, []);

    const onActionFilterChange = useCallback((newValue: SingleValue<IOption>) => {
        pagedListStore.setFilterActionIds(newValue?.value ? String(newValue.value) : '');
        pagedListStore.loadPage(1);
    }, []);

    const filterByActionSelectedValue = pagedListStore.filterByActionOptions().find(op => op.value === pagedListStore.filterActionIds);

    const onDateRangeFilterApply = useCallback((dateRange: IDateRangeISO | null) => {
        /** Convert ISO UTC date range to required format for backend */
        const dateRangeString = dateRange ? `${dateRange.from}/${dateRange.to}` : '';

        pagedListStore.setFilterDateRange(dateRangeString);
        pagedListStore.loadPage(1);
    }, []);

    const getTransactionStatusChipColor = (status: TRANSACTION_STATUSES) => {
        switch (status) {
            case TRANSACTION_STATUSES.REQUESTED:
            case TRANSACTION_STATUSES.CANCELED:
                return CHIP_COLORS.ORANGE;
            case TRANSACTION_STATUSES.FAILED:
                return CHIP_COLORS.RED;
            case TRANSACTION_STATUSES.SUCCESS:
                return CHIP_COLORS.GREEN;
            default:
                return CHIP_COLORS.DEFAULT;
        }
    }

    const getTransactionStatusLabel = (status: TRANSACTION_STATUSES) => {
        switch (status) {
            case TRANSACTION_STATUSES.CANCELED:
                return "CANCELLED"
            case TRANSACTION_STATUSES.FAILED:
                return "FAILED";
            case TRANSACTION_STATUSES.REQUESTED:
                return "REQUESTED";
            case TRANSACTION_STATUSES.SUCCESS:
                return "SUCCESS";
            default:
                return "UNKNOWN";
        }
    }

    return (
        <BasePageLayout
            title="Analytics"
            subTitles={['Transactions']}
            instruction={<>
                <div>
                    <FontBody1>
                        View transactions made by customers who returned items through your portal.
                    </FontBody1>
                </div>
                <div>
                    <FontBody1>
                        For privacy reasons, only customer IDs are displayed.
                    </FontBody1>
                </div>
            </>}
        >
            <ReportBoxLayout>
                <Report
                    type={REPORT_TYPES.BANK_REFUNDS}
                    title="bank refunds"
                    getReportFilters={pagedListStore.getReportFilters}
                />
                <Report
                    type={REPORT_TYPES.TRANSACTIONS}
                    title="transactions"
                    getReportFilters={pagedListStore.getReportFilters}
                />
            </ReportBoxLayout>
            <Card>
                <SearchBar>
                    <SearchInput
                        value={pagedListStore.searchCustomerId ?? ''}
                        onClick={onSearchClick}
                        onChange={onSearchChange}
                        tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>Search by customer ID.</FontBody2>}
                    />
                    <DateRangePickerV2
                        onChange={onDateRangeFilterApply}
                    />
                    <SelectDropdown
                        placeholder='Filter by action'
                        options={pagedListStore.filterByActionOptions()}
                        onChange={onActionFilterChange}
                        value={filterByActionSelectedValue}
                    />
                </SearchBar>
                <Table
                    isLoading={pagedListStore.isLoading}
                    pagination={{
                        onSetPage: pagedListStore.loadPage,
                        page: pagedListStore.page,
                        total: pagedListStore.total,
                        perPage: pagedListStore.perPage
                    }}
                    sort={{
                        onSort: pagedListStore.sortData,
                        by: pagedListStore.sortBy,
                        dir: pagedListStore.sortDir,
                    }}
                    columns={[
                        {
                            content: 'id',
                            sortBy: 'id',
                            sortable: true,
                            width: {
                                value: 0.5,
                                type: 'fr'
                            }
                        },
                        ...(currentAccountStore.isSysAdmin ? [{
                            content: 'store',
                            width: {
                                value: 1.5,
                                type: 'fr'
                            }
                        } as IColumn] : []),
                        {
                            content: 'date & time',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'customer id',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'order id',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'rma id',
                            width: {
                                value: 0.7,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'platform transaction id',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'credits',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: <GiftCardsHeadCellContent />,
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'money',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                        {
                            content: 'action',
                            width: {
                                value: 100,
                                type: 'px'
                            }
                        },
                        {
                            content: 'status',
                            width: {
                                value: 1,
                                type: 'fr'
                            }
                        },
                    ]}
                    totalResultsTitle='Transactions'
                >
                    {pagedListStore.list.map((item: ITransaction) => {
                        const isCreditsAmountNegative = item.transaction_type_id !== TRANSACTION_TYPES.STORE_SPENT && item.transaction_type_id !== TRANSACTION_TYPES.CREDIT_ADJUSTMENT_WITHDRAW;
                        const isGiftCardAmountNegative = item.transaction_type_id !== TRANSACTION_TYPES.STORE_SPENT;
                        const isCashAmountNegative =
                            item.transaction_type_id !== TRANSACTION_TYPES.STORE_SPENT &&
                            item.transaction_type_id !== TRANSACTION_TYPES.PAYMENT_CAPTURE &&
                            item.transaction_type_id !== TRANSACTION_TYPES.PAYMENT_AUTHORIZATION &&
                            item.transaction_type_id !== TRANSACTION_TYPES.INVOICE &&
                            item.transaction_type_id !== TRANSACTION_TYPES.INVOICE_ADJUSTMENT_POSITIVE;
                        const currencyCode = item.currency_code ?? currentPortalStore.currencyCode;
                        
                        return (
                        <TableRow
                        skipWrapingCell={true}
                            key={item.id}
                            cells={[
                                <FontBody2>{item.id}</FontBody2>,
                                ...(currentAccountStore.isSysAdmin ? [<FontBody2>{item.store_name}</FontBody2>] : []),
                                <FontBody2>
                                    <DateTimeV2 {...dateTimeParams} utcDateString={item.created_at} dateTimeView={DATETIME_VIEW.FULL} />
                                </FontBody2>,
                                <div className="transaction-item-text-truncate">
                                    <AHref
                                        target='_blank'
                                        href={item.admin_customer_url}
                                    >
                                        <FontBody2>{item.customer_id}</FontBody2>
                                    </AHref>
                                </div>,
                                <div className="transaction-item-text-truncate">
                                    <FontBody2>{item.order_name ?? item.order_id}</FontBody2>
                                </div>,
                                <FontBody2>{item.rma_id}</FontBody2>,
                                <div className="transaction-item-text-truncate">
                                    <FontBody2>{item.platform_transaction_id}</FontBody2>
                                </div>,
                                <Amount value={item.credits && <Currency amount={isCreditsAmountNegative ? item.credits * -1 : item.credits} currencyCode={currencyCode} />}
                                    negative={isCreditsAmountNegative}
                                    is_incentive={!!item.credits_formatted && item.is_incentive}
                                />,
                                <Amount value={item.gift_card && <Currency amount={isGiftCardAmountNegative ? item.gift_card * -1 : item.gift_card} currencyCode={currencyCode} />}
                                    negative={isGiftCardAmountNegative}
                                    is_incentive={!!item.gift_card_formatted && item.is_incentive}
                                />,
                                <Amount value={item.cash && <Currency amount={isCashAmountNegative ? item.cash * -1 : item.cash} currencyCode={currencyCode} />}
                                    negative={isCashAmountNegative}
                                    transaction_type_id={item.transaction_type_id}
                                />,
                                <TansactionType
                                    transaction_type_id={item.transaction_type_id}
                                    cash={item.cash}
                                    credits={item.credits}
                                    gift_card={item.gift_card}
                                    refund_provider={item.refund_provider}
                                />,
                                !!item.status ?
                                    <Chip
                                        label={getTransactionStatusLabel(item.status)}
                                        color={getTransactionStatusChipColor(item.status)}
                                    />
                                    : '',
                            ]}
                        />
                    )})}
                </Table>
            </Card>
        </BasePageLayout>
    );
});

export default TransactionsPage;
